import { useState, useEffect } from 'react';
import {
  listPromotionalBanners,
  uploadPromotionalBanner,
  deletePromotionalBanner,
  reorderPromotionalBanners,
  togglePromotionalBanner,
  type PromotionalBanner,
} from '@/shared/services/promotionalBannersService';
import { listRestaurants, type Restaurant } from '@/shared/services/restaurantsService';
import { uploadImage, generatePromotionalBannerPath } from '@/shared/services/storage';
import { ImageUpload } from '@/shared/components/ImageUpload';
import { ConfirmModal } from '@/shared/components/ConfirmModal';
import styles from './PromotionalBannersPage.module.css';

export function PromotionalBannersPage() {
  const [banners, setBanners] = useState<PromotionalBanner[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados para modales
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBanner, setDeletingBanner] = useState<PromotionalBanner | null>(null);

  // Estados para formulario de upload
  const [uploadImageFile, setUploadImageFile] = useState<File | null>(null);
  const [uploadImagePreview, setUploadImagePreview] = useState<string | null>(null);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadBanners();
    loadRestaurants();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      setError('');
      const bannersData = await listPromotionalBanners();
      setBanners(bannersData);
    } catch (err: any) {
      console.error('Error al cargar banners:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al cargar los banners');
    } finally {
      setLoading(false);
    }
  };

  const loadRestaurants = async () => {
    try {
      const restaurantsData = await listRestaurants();
      setRestaurants(restaurantsData);
    } catch (err: any) {
      console.error('Error al cargar restaurantes:', err);
      // No mostrar error crítico, solo log
    }
  };

  const handleUploadBanner = async () => {
    if (processing) return;

    if (!uploadImageFile) {
      setError('Debes seleccionar una imagen');
      return;
    }

    if (!selectedRestaurantId) {
      setError('Debes seleccionar un restaurante');
      return;
    }

    try {
      setProcessing(true);
      setError('');

      // Subir imagen a Firebase Storage
      const imagePath = generatePromotionalBannerPath();
      const imageUrl = await uploadImage(uploadImageFile, imagePath);

      // Crear banner
      await uploadPromotionalBanner({
        imageUrl,
        restaurantId: selectedRestaurantId,
      });

      setSuccess('Banner creado exitosamente');
      setShowUploadModal(false);
      resetUploadForm();
      await loadBanners();
    } catch (err: any) {
      console.error('Error al subir banner:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al subir el banner');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteBanner = async () => {
    if (!deletingBanner || processing) return;

    try {
      setProcessing(true);
      setError('');
      await deletePromotionalBanner(deletingBanner.bannerId);
      setSuccess('Banner eliminado exitosamente');
      setShowDeleteModal(false);
      setDeletingBanner(null);
      await loadBanners();
    } catch (err: any) {
      console.error('Error al eliminar banner:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al eliminar el banner');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleBanner = async (banner: PromotionalBanner) => {
    if (processing) return;

    try {
      setProcessing(true);
      setError('');
      const result = await togglePromotionalBanner(banner.bannerId);
      setSuccess(`Banner ${result.isActive ? 'activado' : 'desactivado'} exitosamente`);
      await loadBanners();
    } catch (err: any) {
      console.error('Error al cambiar estado del banner:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al cambiar el estado del banner');
    } finally {
      setProcessing(false);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0 || processing) return;

    const newBanners = [...banners];
    const prevBanner = newBanners[index - 1];
    const currBanner = newBanners[index];
    if (prevBanner && currBanner) {
      newBanners[index - 1] = currBanner;
      newBanners[index] = prevBanner;
    }

    try {
      setProcessing(true);
      setError('');
      const bannerIds = newBanners.map((b) => b.bannerId);
      await reorderPromotionalBanners({ bannerIds });
      setSuccess('Banners reordenados exitosamente');
      await loadBanners();
    } catch (err: any) {
      console.error('Error al reordenar banners:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al reordenar los banners');
    } finally {
      setProcessing(false);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === banners.length - 1 || processing) return;

    const newBanners = [...banners];
    const nextBanner = newBanners[index + 1];
    const currBanner = newBanners[index];
    if (nextBanner && currBanner) {
      newBanners[index + 1] = currBanner;
      newBanners[index] = nextBanner;
    }

    try {
      setProcessing(true);
      setError('');
      const bannerIds = newBanners.map((b) => b.bannerId);
      await reorderPromotionalBanners({ bannerIds });
      setSuccess('Banners reordenados exitosamente');
      await loadBanners();
    } catch (err: any) {
      console.error('Error al reordenar banners:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al reordenar los banners');
    } finally {
      setProcessing(false);
    }
  };

  const resetUploadForm = () => {
    setUploadImageFile(null);
    setUploadImagePreview(null);
    setSelectedRestaurantId('');
  };

  const openDeleteModal = (banner: PromotionalBanner) => {
    setDeletingBanner(banner);
    setShowDeleteModal(true);
  };

  if (loading && banners.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Cargando banners...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Banners Promocionales</h1>
          <p className={styles.subtitle}>Gestiona las imágenes promocionales que aparecen en la app del cliente</p>
        </div>
        <button className={styles.addButton} onClick={() => setShowUploadModal(true)}>
          + Nuevo Banner
        </button>
      </div>

      {error && (
        <div className={styles.alertError} onClick={() => setError('')}>
          {error}
        </div>
      )}
      {success && (
        <div className={styles.alertSuccess} onClick={() => setSuccess('')}>
          {success}
        </div>
      )}

      {banners.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="9" y1="3" x2="9" y2="21" />
            </svg>
          </div>
          <h2>No hay banners promocionales</h2>
          <p>Agrega tu primer banner promocional para que aparezca en la app del cliente</p>
          <button className={styles.addButton} onClick={() => setShowUploadModal(true)}>
            + Crear Primer Banner
          </button>
        </div>
      ) : (
        <div className={styles.bannersGrid}>
          {banners.map((banner, index) => (
            <div key={banner.bannerId} className={styles.bannerCard}>
              <div className={styles.bannerImageContainer}>
                <img src={banner.imageUrl} alt={`Banner ${banner.order}`} className={styles.bannerImage} />
                <div className={styles.bannerOverlay}>
                  <span className={styles.bannerOrder}>#{banner.order}</span>
                  <div className={styles.bannerStatus}>
                    <span className={banner.isActive ? styles.statusActive : styles.statusInactive}>
                      {banner.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
              </div>
              <div className={styles.bannerInfo}>
                <h3 className={styles.bannerRestaurantName}>{banner.restaurantName || 'Restaurante'}</h3>
                <p className={styles.bannerRestaurantId}>ID: {banner.restaurantId}</p>
              </div>
              <div className={styles.bannerActions}>
                <div className={styles.reorderButtons}>
                  <button
                    className={styles.reorderButton}
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0 || processing}
                    title="Mover arriba"
                  >
                    ↑
                  </button>
                  <button
                    className={styles.reorderButton}
                    onClick={() => handleMoveDown(index)}
                    disabled={index === banners.length - 1 || processing}
                    title="Mover abajo"
                  >
                    ↓
                  </button>
                </div>
                <button
                  className={`${styles.toggleButton} ${banner.isActive ? styles.toggleActive : styles.toggleInactive}`}
                  onClick={() => handleToggleBanner(banner)}
                  disabled={processing}
                >
                  {banner.isActive ? 'Desactivar' : 'Activar'}
                </button>
                <button
                  className={styles.deleteButton}
                  onClick={() => openDeleteModal(banner)}
                  disabled={processing}
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Upload */}
      {showUploadModal && (
        <div className={styles.modalOverlay} onClick={() => !processing && setShowUploadModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Nuevo Banner Promocional</h2>
              <button
                className={styles.modalClose}
                onClick={() => !processing && (setShowUploadModal(false), resetUploadForm())}
                disabled={processing}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Imagen del Banner *</label>
                <ImageUpload
                  preview={uploadImagePreview || undefined}
                  onChange={(file) => {
                    setUploadImageFile(file);
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setUploadImagePreview(reader.result as string);
                      };
                      reader.readAsDataURL(file);
                    } else {
                      setUploadImagePreview(null);
                    }
                  }}
                  label="Seleccionar imagen"
                />
                <p className={styles.helpText}>
                  Recomendado: 1920x1080px (16:9). La imagen se mostrará en el carrusel de la app del cliente.
                </p>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Restaurante *</label>
                <select
                  className={styles.select}
                  value={selectedRestaurantId}
                  onChange={(e) => setSelectedRestaurantId(e.target.value)}
                  disabled={processing}
                >
                  <option value="">Selecciona un restaurante</option>
                  {restaurants
                    .filter((r) => r.status === 'activo')
                    .map((restaurant) => (
                      <option key={restaurant.restaurantId} value={restaurant.restaurantId}>
                        {restaurant.name}
                      </option>
                    ))}
                </select>
                <p className={styles.helpText}>
                  Al hacer clic en este banner, los usuarios serán redirigidos a este restaurante.
                </p>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.modalCancel}
                onClick={() => !processing && (setShowUploadModal(false), resetUploadForm())}
                disabled={processing}
              >
                Cancelar
              </button>
              <button
                className={styles.modalSubmit}
                onClick={handleUploadBanner}
                disabled={processing || !uploadImageFile || !selectedRestaurantId}
              >
                {processing ? 'Subiendo...' : 'Crear Banner'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteBanner}
        title="Eliminar Banner"
        message={`¿Estás seguro de que deseas eliminar este banner promocional? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmButtonVariant="danger"
        isLoading={processing}
      />
    </div>
  );
}

